/**
 * A radial tree lifted into z by depth, with reference edges as chords across it.
 *
 * Angular space is divided by leaf count rather than by child count, which is the whole difference
 * between a readable tree and one where a subtree of forty applications gets the same wedge as a
 * subtree of one. Each node sits at the center of its own wedge, so a parent is always above the
 * middle of its children and the structure can be read without following lines.
 *
 * A forest is laid out as one tree with no root: the roots share the full circle in proportion to
 * their leaves, so separate workspaces come out as separate sectors rather than overlapping.
 */

import type { Hierarchy } from '../graph/topology';
import type { Vec3 } from '../types';

export type ConeTreeArgs = {
  hierarchy: Hierarchy;
  nodeCount: number;
  /** Radius added per level of depth. */
  radialStep: number;
  /** Drop in z per level of depth. Roots sit at 0 and leaves at the bottom. */
  depthStep: number;
};

/**
 * Radius the roots of a forest sit at, as a fraction of `radialStep`.
 *
 * A single-rooted tree puts its root on the axis, which is right - there is nothing for it to be
 * separated from. A forest cannot: every root is at depth 0, so a radius of 0 stacks all of them
 * at exactly one point, and the demo estate's six roots came out as one node with six sets of
 * children fanning off it. Holding them off the axis gives each root its wedge at the top as well
 * as below.
 */
const FOREST_ROOT_RADIUS = 0.55;

export const toConeTreePositions = ({
  hierarchy,
  nodeCount,
  radialStep,
  depthStep,
}: ConeTreeArgs): Vec3[] => {
  const { childIndices, rootIndices, leafCounts } = hierarchy;
  const positions = new Array<Vec3>(nodeCount).fill([0, 0, 0]);

  const totalLeaves = rootIndices.reduce(
    (total, root) => total + Math.max(1, leafCounts[root]),
    0
  );

  type Frame = {
    index: number;
    depth: number;
    /** Half-open angular wedge this subtree owns, in radians. */
    startAngle: number;
    endAngle: number;
  };

  const rootOffset = rootIndices.length > 1 ? FOREST_ROOT_RADIUS : 0;
  const stack: Frame[] = [];
  /**
   * Placed once, whatever `childIndices` says.
   *
   * `toHierarchy` survives a `parentId` cycle by promoting its members to roots, but it leaves the
   * cycle's own edges in `childIndices` - so for `A.parent = B, B.parent = A` both are roots *and*
   * each is the other's child. Without this the stack never drains: pop B, push A, pop A, push B.
   * That is worse than the stack overflow the iterative walk exists to avoid, because a frozen tab
   * inside `buildNetworkLayout` produces no error and no frame at all.
   *
   * Every other walk over `childIndices` guards the same way - `toTopDownOrder`,
   * `toDepthFirstOrder`, `toSectorLeaves`, `toAncestorPath`.
   */
  const seen = new Set<number>();
  let rootAngle = 0;

  for (const root of rootIndices) {
    const share = Math.max(1, leafCounts[root]) / Math.max(1, totalLeaves);
    const span = share * Math.PI * 2;
    stack.push({
      index: root,
      depth: 0,
      startAngle: rootAngle,
      endAngle: rootAngle + span,
    });
    rootAngle += span;
  }

  while (stack.length > 0) {
    const frame = stack.pop();
    if (!frame) break;
    const { index, depth, startAngle, endAngle } = frame;
    if (seen.has(index)) continue;
    seen.add(index);

    const angle = (startAngle + endAngle) / 2;
    const radius = (depth + rootOffset) * radialStep;
    positions[index] = [
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      -depth * depthStep,
    ];

    const children = childIndices[index];
    if (children.length === 0) continue;

    /* A root's children take the root's own full wedge. Below that the wedge narrows only by
       subdivision, which is what makes the tree flare outward: the arc length a wedge covers grows
       with radius, so a constant angular share buys more room at every level. */
    const childLeaves = children.reduce(
      (total, child) => total + Math.max(1, leafCounts[child]),
      0
    );
    let cursor = startAngle;

    for (const child of children) {
      const span =
        ((endAngle - startAngle) * Math.max(1, leafCounts[child])) /
        Math.max(1, childLeaves);
      stack.push({
        index: child,
        depth: depth + 1,
        startAngle: cursor,
        endAngle: cursor + span,
      });
      cursor += span;
    }
  }

  return positions;
};

/**
 * The parent-child segments, which are the tree's own structure rather than data.
 *
 * Takes the pairs rather than the hierarchy, so a caller holding both can hand the same list to
 * this and to `NetworkLayout.guideLinks` - which is what lets a movement redraw the skeleton from
 * the positions the marks are actually at.
 */
export const toHierarchyGuides = (
  links: readonly { source: number; target: number }[],
  positions: readonly Vec3[]
): Vec3[][] =>
  links.map(({ source, target }): Vec3[] => [
    positions[source],
    positions[target],
  ]);
